"use client";

import { evaluateSync } from "@mdx-js/mdx";
import type { MDXComponents } from "mdx/types";
import type { AnchorHTMLAttributes, ComponentType, ReactNode } from "react";
import { Fragment, useMemo } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { GailBlue } from "@/components/common/GailBlue";
import {
  extractBlogMdxSource,
  isPuckInlineEditorValue,
  renderBlogMdxFallback,
} from "@/utils/blogMdxSource";
import { transformGailBlueMarkdown } from "@/utils/gailBlueMarkdown";
import { useMDXComponents } from "../../../mdx-components";

type RuntimeMdxProps = {
  source: string;
  components?: MDXComponents;
  inline?: boolean;
};

type MdxModule = {
  default: ComponentType<{ components?: MDXComponents }>;
};

export function normalizeMdxSource(source: unknown): string {
  return extractBlogMdxSource(source);
}

function escapeMdxJsx(source: string): string {
  return source.replace(/[<>]/g, (char) => (char === "<" ? "&lt;" : "&gt;"));
}

function SafeMdxLink({
  href,
  children,
  ...props
}: Readonly<AnchorHTMLAttributes<HTMLAnchorElement>>) {
  const isExternal = href ? /^https?:\/\//.test(href) : false;

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className="text-gail-secondary underline hover:text-black"
      {...props}
    >
      {children}
    </a>
  );
}

function compileMdx(source: string): ComponentType<{
  components?: MDXComponents;
}> {
  const prepared = transformGailBlueMarkdown(escapeMdxJsx(source));
  const evaluated = evaluateSync(prepared, {
    Fragment,
    jsx,
    jsxs,
    development: false,
  }) as MdxModule;

  return evaluated.default;
}

function InlineMdxParagraph({ children }: Readonly<{ children: ReactNode }>) {
  return <>{children}</>;
}

export default function RuntimeMdx({
  source,
  components,
  inline = false,
}: Readonly<RuntimeMdxProps>) {
  const normalizedSource = normalizeMdxSource(source);

  const mdxComponents = useMDXComponents({
    a: SafeMdxLink,
    GailBlue,
    ...(inline ? { p: InlineMdxParagraph } : {}),
    ...components,
  });

  const Content = useMemo(() => {
    if (!normalizedSource.trim()) return null;
    return compileMdx(normalizedSource);
  }, [normalizedSource]);

  if (!Content) return null;

  // MDX content is compiled from trusted Studio/admin-authored rich text.
  // eslint-disable-next-line react-hooks/static-components
  return <Content components={mdxComponents} />;
}

type BlogMdxFieldProps = {
  value: unknown;
  isEditing?: boolean;
  inline?: boolean;
};

/** Renders markdown via RuntimeMdx, or Puck inline editor nodes when editing. */
export function BlogMdxField({
  value,
  isEditing = false,
  inline = false,
}: Readonly<BlogMdxFieldProps>) {
  if (isEditing && isPuckInlineEditorValue(value)) {
    return <>{value}</>;
  }

  const source = extractBlogMdxSource(value);

  if (source.trim()) {
    return <RuntimeMdx source={source} inline={inline} />;
  }

  return <>{renderBlogMdxFallback(value)}</>;
}
